import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import { useSuperAdminAuth } from "../context/SuperAdminAuthContext";
import saApi from "../lib/saApi";
import { Building2, Users, LayoutDashboard, Database, Loader2, Search, Eye, Loader as LoaderIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function SATenants() {
  const { tenants, loading } = useSuperAdminTenant();
  const { has } = useSuperAdminAuth();
  const canImpersonate = has("impersonate");
  const [q, setQ] = useState("");
  const [detailed, setDetailed] = useState(null);
  const [impersonating, setImpersonating] = useState(null); // tenant.id currently starting
  // Freshly fetch on mount so counts reflect current state even if provider cache is stale.
  useEffect(() => { saApi.get("/tenants").then(({ data }) => setDetailed(data.items || [])).catch(() => {}); }, []);
  const rows = (detailed || tenants).filter((t) => !q || (t.name || "").toLowerCase().includes(q.toLowerCase()));

  const impersonate = async (t) => {
    if (!window.confirm(`Start a READ-ONLY impersonation of "${t.name}"? Your current client session (if any) will be replaced. Session lasts 30 minutes.`)) return;
    setImpersonating(t.id);
    try {
      const { data } = await saApi.post(`/impersonate/${t.id}`);
      toast.success(`Impersonating ${data.tenant.name} — opening in this tab`);
      // Small delay so the sonner is visible before the hard nav.
      setTimeout(() => { window.location.href = data.redirect || "/"; }, 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonation failed");
      setImpersonating(null);
    }
  };
  return (
    <SuperAdminShell title="Tenants">
      <div className="px-5 md:px-8 py-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="sa-tenants-search"
              placeholder="Search tenants…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
          </div>
          <span className="text-xs text-white/50">{rows.length} of {(detailed || tenants).length}</span>
        </div>
        {loading && !detailed ? (
          <div className="flex items-center gap-2 text-white/60"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((t) => (
              <div key={t.id} data-testid={`sa-tenant-card-${t.id}`}
                   className="rounded-2xl p-5 border border-white/10 backdrop-blur-md hover:border-cyan-400/40 transition"
                   style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))" }}>
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-400/10">
                    <Building2 className="w-4 h-4 text-cyan-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-white font-medium truncate">{t.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 truncate">{t.slug || t.id}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <Stat icon={Users} label="Users" value={t.counts?.users ?? "—"} />
                  <Stat icon={LayoutDashboard} label="Dashboards" value={t.counts?.dashboards ?? "—"} />
                  <Stat icon={Database} label="Datasets" value={t.counts?.datasets ?? "—"} />
                </div>
                {t.ai_key_policy && (
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40">
                    AI key: <span className={t.ai_key_policy === "mandatory" ? "text-amber-300" : "text-white/60"}>
                      {t.ai_key_policy}
                    </span>
                  </div>
                )}
                <Button
                  data-testid={`sa-tenant-impersonate-${t.id}`}
                  size="sm"
                  variant="ghost"
                  disabled={!canImpersonate || impersonating === t.id}
                  onClick={() => impersonate(t)}
                  title={canImpersonate ? "Open a read-only shadow session for support debugging" : "Your scope does not include the 'impersonate' feature"}
                  className="w-full mt-3 h-8 text-cyan-300 hover:text-white hover:bg-cyan-400/10 border border-cyan-400/20 hover:border-cyan-400/40 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {impersonating === t.id ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Opening…</>
                  ) : (
                    <><Eye className="w-3.5 h-3.5 mr-1.5" />Impersonate (read-only)</>
                  )}
                </Button>
              </div>
            ))}
            {rows.length === 0 && (
              <div className="text-white/50 text-sm">No tenants match.</div>
            )}
          </div>
        )}
      </div>
    </SuperAdminShell>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/40">
        <Icon className="w-3 h-3" />{label}
      </div>
      <div className="text-lg text-white mt-0.5">{value}</div>
    </div>
  );
}
